import { getSupabase } from '../services/supabase';

const EARNED_KEY = 'earnedMarks';

const MARK_DETAILS = {
  // ── Kitchen ────────────────────────────────────
  first_mise: {
    name: 'First Mise',
    description: 'Stock the pantry. Discipline starts before the heat.',
    domain: 'kitchen',
    rarity: 'common',
  },
  the_saucier: {
    name: 'The Saucier',
    description: 'Twenty-five recipes catalogued. The repertoire takes shape.',
    domain: 'kitchen',
    rarity: 'uncommon',
  },
  zero_spoil: {
    name: 'Zero Spoil',
    description: 'Every item used before expiry this week. Nothing wasted.',
    domain: 'kitchen',
    rarity: 'uncommon',
  },
  the_frugal: {
    name: 'The Frugal',
    description: 'Under budget, four weeks running. The margin is the mastery.',
    domain: 'kitchen',
    rarity: 'rare',
  },

  // ── Protocol ───────────────────────────────────
  first_bar: {
    name: 'First Bar',
    description: 'Lift logged. The first rep is the hardest.',
    domain: 'protocol',
    rarity: 'common',
  },
  the_microcycle: {
    name: 'The Microcycle',
    description: 'One workout per week, four weeks unbroken. The protocol holds.',
    domain: 'protocol',
    rarity: 'rare',
  },
  the_plate: {
    name: 'The Plate',
    description: 'Bodyweight on the bar. The first real milestone.',
    domain: 'protocol',
    rarity: 'rare',
  },
  volume_threshold: {
    name: 'Volume Threshold',
    description: 'Ten thousand pounds moved in a single session. The work speaks.',
    domain: 'protocol',
    rarity: 'uncommon',
  },
  the_discipline: {
    name: 'The Discipline',
    description: 'Seven workouts in seven days. Rest is earned, not defaulted.',
    domain: 'protocol',
    rarity: 'rare',
  },

  // ── Crossover ──────────────────────────────────
  the_synchronist: {
    name: 'The Synchronist',
    description: 'Meals planned and workout logged on the same day. Both stations running.',
    domain: 'crossover',
    rarity: 'uncommon',
  },
  the_architect: {
    name: 'The Architect',
    description: 'Inventory stocked, meals planned, workout logged, budget tracked — all in one day.',
    domain: 'crossover',
    rarity: 'lifetime',
  },

  // ── Counter-balance ────────────────────────────
  the_forgiveness: {
    name: 'The Forgiveness',
    description: 'Discipline is the recovery, not the perfection.',
    domain: 'counter',
    rarity: 'uncommon',
  },
  the_provisioner: {
    name: 'The Provisioner',
    description: 'Shopping list cleared and inventory restocked. The prep is the protocol.',
    domain: 'counter',
    rarity: 'common',
  },
};

const DOMAINS = {
  kitchen:   { label: 'Kitchen',         order: 1 },
  protocol:  { label: 'Protocol',        order: 2 },
  crossover: { label: 'Crossover',       order: 3 },
  counter:   { label: 'Counter-balance', order: 4 },
};

export const getMarkDetails = (id) => MARK_DETAILS[id];
export const getAllMarkIds = () => Object.keys(MARK_DETAILS);
export const getDomains = () => DOMAINS;

export const getHighestRank = (earnedIds) => {
  const RARITY_WEIGHT = { common: 1, uncommon: 2, rare: 3, lifetime: 4 };
  let best = null;
  let bestWeight = 0;
  for (const id of earnedIds) {
    const m = MARK_DETAILS[id];
    if (!m) continue;
    const w = RARITY_WEIGHT[m.rarity] || 0;
    if (w > bestWeight) {
      bestWeight = w;
      best = m;
    }
  }
  return best;
};

// ── Utility ────────────────────────────────────

const getDate = (entry) => {
  const raw = entry?.date || entry?.createdAt || entry?.timestamp;
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

const toDateKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const isoWeekKey = (d) => {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil((((t - yearStart) / 86400000) + 1) / 7);
  return t.getUTCFullYear() * 100 + weekNum;
};

const today = () => {
  const d = new Date();
  return toDateKey(d);
};

// ── Evaluators ─────────────────────────────────

const MARK_EVALUATORS = {
  // Kitchen
  first_mise: (s) => (s.inventory?.length || 0) >= 1,

  the_saucier: (s) => (s.recipes?.length || 0) >= 25,

  zero_spoil: (s) => {
    const inv = s.inventory || [];
    if (inv.length === 0) return false;
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 86400000);
    const withExpiry = inv.filter((i) => i.expiryDate);
    if (withExpiry.length === 0) return false;
    return withExpiry.every((i) => {
      const exp = new Date(i.expiryDate);
      return exp > now || exp < weekAgo;
    });
  },

  the_frugal: (s) => {
    const b = s.budget;
    if (!b?.weeklyBudget || !b?.expenses?.length) return false;
    const expenses = b.expenses;
    const now = new Date();
    const fourWeeksAgo = new Date(now.getTime() - 28 * 86400000);
    const recentExpenses = expenses.filter((e) => {
      const d = new Date(e.date);
      return d >= fourWeeksAgo;
    });
    if (recentExpenses.length < 4) return false;
    const weeklyLimit = b.period === 'monthly' ? b.weeklyBudget / 4
      : b.period === 'biweekly' ? b.weeklyBudget / 2
      : b.weeklyBudget;
    for (let w = 0; w < 4; w++) {
      const start = new Date(now.getTime() - (w + 1) * 7 * 86400000);
      const end = new Date(now.getTime() - w * 7 * 86400000);
      const weekTotal = recentExpenses
        .filter((e) => { const d = new Date(e.date); return d >= start && d < end; })
        .reduce((sum, e) => sum + (e.amount || 0), 0);
      if (weekTotal > weeklyLimit) return false;
    }
    return true;
  },

  // Protocol
  first_bar: (s) => (s.workoutLog?.length || 0) >= 1,

  the_microcycle: (s) => {
    const log = s.workoutLog || [];
    if (log.length < 4) return false;
    const weeks = new Set();
    for (const w of log) {
      const d = getDate(w);
      if (d) weeks.add(isoWeekKey(d));
    }
    if (weeks.size < 4) return false;
    const sorted = [...weeks].sort((a, b) => a - b);
    let run = 1;
    for (let i = 1; i < sorted.length; i++) {
      const diff = sorted[i] - sorted[i - 1];
      if (diff === 1 || diff === 89) run++;
      else run = 1;
      if (run >= 4) return true;
    }
    return false;
  },

  the_plate: (s) => {
    const log = s.workoutLog || [];
    const bw = s.biometrics?.weight;
    if (!bw || bw <= 0 || log.length === 0) return false;
    return log.some((entry) => (entry.weight || 0) >= bw);
  },

  volume_threshold: (s) => {
    const log = s.workoutLog || [];
    if (log.length === 0) return false;
    const byDate = {};
    for (const entry of log) {
      const d = entry.date || 'unknown';
      if (!byDate[d]) byDate[d] = 0;
      byDate[d] += (entry.sets || 1) * (entry.reps || 1) * (entry.weight || 0);
    }
    return Object.values(byDate).some((vol) => vol >= 10000);
  },

  the_discipline: (s) => {
    const log = s.workoutLog || [];
    if (log.length < 7) return false;
    const dates = [...new Set(log.map((w) => w.date).filter(Boolean))].sort();
    let run = 1;
    for (let i = 1; i < dates.length; i++) {
      const prev = new Date(dates[i - 1]);
      const curr = new Date(dates[i]);
      const diff = (curr - prev) / 86400000;
      if (diff === 1) { run++; if (run >= 7) return true; }
      else run = 1;
    }
    return false;
  },

  // Crossover
  the_synchronist: (s) => {
    const log = s.workoutLog || [];
    const plan = s.mealPlan || {};
    if (log.length === 0) return false;
    const workoutDates = new Set(log.map((w) => w.date).filter(Boolean));
    for (const [dateKey, meals] of Object.entries(plan)) {
      if (Array.isArray(meals) && meals.length > 0 && workoutDates.has(dateKey)) return true;
    }
    return false;
  },

  the_architect: (s) => {
    const todayKey = today();
    const hasInventory = (s.inventory?.length || 0) >= 1;
    const hasMealToday = (() => {
      const plan = s.mealPlan || {};
      const meals = plan[todayKey];
      return Array.isArray(meals) && meals.length > 0;
    })();
    const hasWorkoutToday = (s.workoutLog || []).some((w) => w.date === todayKey);
    const hasBudgetEntry = (s.budget?.expenses || []).some((e) => {
      const d = new Date(e.date);
      return toDateKey(d) === todayKey;
    });
    return hasInventory && hasMealToday && hasWorkoutToday && hasBudgetEntry;
  },

  // Counter-balance
  the_forgiveness: (s) => {
    const log = s.workoutLog || [];
    if (log.length < 2) return false;
    const dates = log.map(getDate).filter(Boolean).sort((a, b) => a - b);
    for (let i = 1; i < dates.length; i++) {
      const gap = (dates[i] - dates[i - 1]) / 86400000;
      if (gap >= 3 && gap <= 7) return true;
    }
    return false;
  },

  the_provisioner: (s) => {
    return (s.shoppingList?.length || 0) === 0 && (s.inventory?.length || 0) >= 5;
  },
};

// ── Public API ─────────────────────────────────

export const evaluateMarks = (state, alreadyEarned = []) => {
  const earned = new Set(alreadyEarned);
  const newlyEarned = [];
  for (const [id, evaluator] of Object.entries(MARK_EVALUATORS)) {
    if (earned.has(id)) continue;
    try {
      if (evaluator(state)) newlyEarned.push(id);
    } catch {}
  }
  return newlyEarned;
};

export const getLocalEarnedMarks = () => {
  try {
    return JSON.parse(localStorage.getItem(EARNED_KEY)) || [];
  } catch {
    return [];
  }
};

export const setLocalEarnedMarks = (ids) => {
  localStorage.setItem(EARNED_KEY, JSON.stringify([...new Set(ids)]));
};

export const recordMarkRemote = async (markId, context = {}) => {
  const db = await getSupabase();
  const { data: { user } } = await db.auth.getUser();
  if (!user) return;
  const { error } = await db
    .from('marks_earned')
    .insert({ user_id: user.id, mark_id: markId, context });
  if (error && error.code !== '23505') throw error;
};

export const fetchEarnedMarks = async () => {
  const db = await getSupabase();
  const { data: { user } } = await db.auth.getUser();
  if (!user) return [];
  const { data, error } = await db
    .from('marks_earned')
    .select('*')
    .eq('user_id', user.id)
    .order('earned_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

// ── Daily Log (Service Calendar data) ──────────

export const computeDailyFlags = (state) => {
  const todayKey = today();
  const hasInventory = (state.inventory?.length || 0) >= 1;
  const hasWorkout = (state.workoutLog || []).some((w) => w.date === todayKey);
  const hasBudget = (state.budget?.expenses || []).some((e) => {
    const d = new Date(e.date);
    return toDateKey(d) === todayKey;
  });
  const hasMeals = (() => {
    const meals = (state.mealPlan || {})[todayKey];
    return Array.isArray(meals) && meals.length > 0;
  })();
  return {
    date: todayKey,
    inventoryLogged: hasInventory,
    workoutDone: hasWorkout,
    budgetTracked: hasBudget,
    mealsPlanned: hasMeals,
  };
};

const DAILY_LOG_KEY = 'dailyLog';

export const getLocalDailyLog = () => {
  try {
    return JSON.parse(localStorage.getItem(DAILY_LOG_KEY)) || {};
  } catch {
    return {};
  }
};

export const setLocalDailyLog = (log) => {
  localStorage.setItem(DAILY_LOG_KEY, JSON.stringify(log));
};

export const recordDailyLog = (state) => {
  const flags = computeDailyFlags(state);
  const log = getLocalDailyLog();
  const existing = log[flags.date] || {};
  log[flags.date] = {
    ...existing,
    inventoryLogged: existing.inventoryLogged || flags.inventoryLogged,
    workoutDone: existing.workoutDone || flags.workoutDone,
    budgetTracked: existing.budgetTracked || flags.budgetTracked,
    mealsPlanned: existing.mealsPlanned || flags.mealsPlanned,
  };
  setLocalDailyLog(log);
  return log;
};

// ── Daily Log Cloud Sync ──────────────────────

export const syncDailyLogRemote = async (state) => {
  const db = await getSupabase();
  const { data: { user } } = await db.auth.getUser();
  if (!user) return;
  const flags = computeDailyFlags(state);
  await db.from('daily_log').upsert(
    {
      user_id: user.id,
      log_date: flags.date,
      inventory_logged: flags.inventoryLogged,
      workout_done: flags.workoutDone,
      budget_under: flags.budgetTracked,
      macros_hit: flags.mealsPlanned,
    },
    { onConflict: 'user_id,log_date' },
  );
};

// ── Percentile Fetch ──────────────────────────

export const fetchPercentile = async (statType = 'workout_days') => {
  const db = await getSupabase();
  const { data: { user } } = await db.auth.getUser();
  if (!user) return null;
  const { data, error } = await db.rpc('get_user_percentile', {
    p_user_id: user.id,
    p_stat_type: statType,
  });
  if (error) return null;
  return data;
};

// ── Mark Progress (Goal Gradient) ─────────────
// Returns { current, threshold, pct, visible } or null.
// `visible` = true only when >= 80% complete (cognitive goal-gradient).

const PROGRESS_EVALUATORS = {
  the_saucier: (s) => ({ current: s.recipes?.length || 0, threshold: 25 }),
  the_discipline: (s) => {
    const log = s.workoutLog || [];
    const dates = [...new Set(log.map((w) => w.date).filter(Boolean))].sort();
    let best = 0;
    let run = 1;
    for (let i = 1; i < dates.length; i++) {
      const diff = (new Date(dates[i]) - new Date(dates[i - 1])) / 86400000;
      if (diff === 1) { run++; best = Math.max(best, run); }
      else run = 1;
    }
    best = Math.max(best, run);
    return { current: Math.min(best, 7), threshold: 7 };
  },
  volume_threshold: (s) => {
    const log = s.workoutLog || [];
    const byDate = {};
    for (const entry of log) {
      const d = entry.date || 'unknown';
      if (!byDate[d]) byDate[d] = 0;
      byDate[d] += (entry.sets || 1) * (entry.reps || 1) * (entry.weight || 0);
    }
    const best = Math.max(0, ...Object.values(byDate));
    return { current: Math.round(best), threshold: 10000 };
  },
  the_microcycle: (s) => {
    const log = s.workoutLog || [];
    const weeks = new Set();
    for (const w of log) {
      const d = getDate(w);
      if (d) weeks.add(isoWeekKey(d));
    }
    const sorted = [...weeks].sort((a, b) => a - b);
    let best = 1;
    let run = 1;
    for (let i = 1; i < sorted.length; i++) {
      const diff = sorted[i] - sorted[i - 1];
      if (diff === 1 || diff === 89) { run++; best = Math.max(best, run); }
      else run = 1;
    }
    best = weeks.size > 0 ? Math.max(best, run) : 0;
    return { current: Math.min(best, 4), threshold: 4 };
  },
};

export const getMarkProgress = (markId, state) => {
  const eval_ = PROGRESS_EVALUATORS[markId];
  if (!eval_) return null;
  try {
    const { current, threshold } = eval_(state);
    const pct = Math.min(1, current / threshold);
    return { current, threshold, pct, visible: pct >= 0.6 };
  } catch {
    return null;
  }
};

// ── Marks summary for AI prompt injection ─────

export const getMarksContextForAI = (earnedIds) => {
  if (!earnedIds || earnedIds.length === 0) return '';
  const lines = earnedIds.map((id) => {
    const m = MARK_DETAILS[id];
    return m ? `${m.name} (${m.domain})` : null;
  }).filter(Boolean);
  if (lines.length === 0) return '';
  return `\nThe athlete has earned these discipline marks: ${lines.join(', ')}. Factor their demonstrated consistency into your recommendations.`;
};
